// corporateExporter.ts — exportación XLSX con plantilla corporativa Axon.
// Usa ExcelJS (no SheetJS) para preservar el formato de la plantilla.
// Genera: Portada + Índice + una hoja por cada pestaña seleccionada.

import ExcelJS from "exceljs";
import type { NbSignal } from "./jsonContracts";

const BASE_URL = import.meta.env.BASE_URL;

export interface CorpTab {
  id:      string;
  title:   string;
  ied:     string;
  rows:    NbSignal[];
}

// ── Lógica de negocio: clasificar el TIPO de la señal ───────────────────────
function clasificarTipo(nombre: string): "ME FL" | "DC" | "DP" | "SP" {
  const t = nombre.toLowerCase();
  const esMedida = /\b(corriente|tensi[oó]n|voltaje|potencia|frecuencia|i[abc]\b|v(?:a|b|c|ab|bc|ca)\b|amperio|vatio|var\b|factor de potencia|hz)\b/.test(t);
  if (esMedida) return "ME FL";
  if (/\b(comando|mando|orden)\b/.test(t)) return "DC";
  if (/\b(posici[oó]n|estado de(?:l)? (?:interruptor|seccionador|cuchilla))\b/.test(t)) return "DP";
  return "SP";
}

// ── Lógica OFF / ON según el tipo ───────────────────────────────────────────
function offOn(tipo: string): [string, string] {
  switch (tipo) {
    case "ME FL": return ["", ""];
    case "DC":    return ["Abrir", "Cerrar"];
    case "DP":    return ["Abierto", "Cerrado"];
    default:      return ["Normal", "Falla"];   // SP
  }
}

// ── Clonar la hoja modelo conservando formato, merges y dimensiones ─────────
function cloneSheet(wb: ExcelJS.Workbook, src: ExcelJS.Worksheet, newName: string): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(newName);
  src.columns.forEach((col, i) => {
    if (col && col.width) ws.getColumn(i + 1).width = col.width;
  });
  src.eachRow({ includeEmpty: true }, (row, rn) => {
    const newRow = ws.getRow(rn);
    newRow.height = row.height;
    row.eachCell({ includeEmpty: true }, (cell, cn) => {
      const nc = newRow.getCell(cn);
      nc.value = cell.value;
      if (cell.style) nc.style = JSON.parse(JSON.stringify(cell.style));
    });
    newRow.commit();
  });
  const merges: string[] = (src.model as any).merges || [];
  merges.forEach(m => { try { ws.mergeCells(m); } catch (e) { /* solapados */ } });
  return ws;
}

function sanitizeSheetName(name: string): string {
  // Excel: máx 31 chars, sin  : \ / ? * [ ]
  return name.replace(/[:\\/?*\[\]]/g, "_").substring(0, 31) || "Hoja";
}

/**
 * Exporta el archivo corporativo con las pestañas indicadas.
 * @param tabs      pestañas a incluir (en orden)
 * @param baseName  nombre del archivo de salida (sin extensión)
 */
export async function exportCorporateXlsx(tabs: CorpTab[], baseName: string): Promise<void> {
  // Cargar la plantilla embebida desde public/
  const resp = await fetch(`${BASE_URL}plantilla_corporativa.xlsx`);
  const buf  = await resp.arrayBuffer();
  const wb   = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);

  const tpl    = wb.getWorksheet("_SIGNAL_TPL_");
  const indice = wb.getWorksheet("índice");
  if (!tpl || !indice) throw new Error("Plantilla corporativa inválida (faltan hojas modelo)");

  // Evitar nombres de hoja duplicados
  const usados = new Set<string>(wb.worksheets.map(w => w.name));

  tabs.forEach((tab, idx) => {
    let nombre = sanitizeSheetName(tab.title);
    let n = nombre, k = 2;
    while (usados.has(n)) { n = sanitizeSheetName(`${nombre} (${k++})`); }
    usados.add(n);

    const ws = cloneSheet(wb, tpl, n);
    ws.getCell("A1").value = tab.title;   // fila 1 = nombre de la pestaña

    // Filas de datos desde la 12
    tab.rows.forEach((s, i) => {
      const r = 12 + i;
      const tipo = clasificarTipo(s.signal);
      const [off, on] = offOn(tipo);
      ws.getCell(`A${r}`).value = i + 1;        // Ítem
      ws.getCell(`B${r}`).value = "Internas";    // Borne origen
      ws.getCell(`C${r}`).value = "-";           // ENTR./SAL. BINARIA
      ws.getCell(`D${r}`).value = "";            // PRIO
      ws.getCell(`E${r}`).value = tab.ied;       // Equipo = IED
      ws.getCell(`F${r}`).value = "";            // Nombre corto
      ws.getCell(`G${r}`).value = s.signal;      // Nombre de señal
      ws.getCell(`H${r}`).value = tipo;          // TIPO
      ws.getCell(`I${r}`).value = off;           // OFF
      ws.getCell(`J${r}`).value = on;            // ON
      ws.getCell(`K${r}`).value = "";            // Alarma-Evento
      ws.getCell(`L${r}`).value = s.mmsNode;     // Maestro 61850 principal
      ws.getCell(`M${r}`).value = "";            // Maestro 61850 redundante
      ws.getCell(`N${r}`).value = "";            // Tipo (IEC104)
      ws.getCell(`O${r}`).value = "";            // IEC 104
      // P/Q/R (validación) se dejan como están en la plantilla
    });

    // Índice: una fila por pestaña desde la 11
    const idxRow = 11 + idx;
    indice.getCell(`A${idxRow}`).value = String(idx + 1).padStart(2, "0");
    indice.getCell(`B${idxRow}`).value = tab.title;
  });

  // Quitar la hoja modelo del resultado
  wb.removeWorksheet(tpl.id);

  // Descargar
  const out = await wb.xlsx.writeBuffer();
  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const safe = (baseName || "axon-tags").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_");
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: `${safe}.xlsx` });
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
